import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FiX } from 'react-icons/fi';
import { loadStripe } from '@stripe/stripe-js';
import serviceIcons from '../../../../helpers/serviceIcons';
import SublyApi from '../../../../helpers/Api';
import { STRIPE_PUBLISHABLE_KEY } from '../../../../config';
import ConfirmationDialog from './ConfirmationDialog';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const AddServiceDrawer = ({ open, onClose, availablePrices, setLoading }) => {
	const token = useSelector((st) => st.user.token);
	const currentUser = useSelector((st) => st.user.currentUser);
	const [selectedPrice, setSelectedPrice] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);

	const handleServiceClick = (price) => {
		setSelectedPrice(price);
		setDialogOpen(true);
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
		setSelectedPrice(null);
	};

	const handleSubscribe = async () => {
		if (!selectedPrice) return;

		setLoading(true);
		setDialogOpen(false);
		onClose();

		try {
			const stripe = await stripePromise;

			const response = await SublyApi.createCheckoutSession(
				{ priceId: selectedPrice.id, customerId: currentUser.customerId },
				token
			);

			const sessionId = response.sessionId;

			const result = await stripe.redirectToCheckout({
				sessionId: sessionId,
			});

			if (result.error) {
				setLoading(false);
				alert('Oh Darn! Something went wrong when redirecting. Please alert admin');
			}
		} catch (err) {
			console.error(err);
			setLoading(false);
			alert('Oh Darn! Something went wrong. Please alert admin');
		}
	};

	if (!open) return null;

	return (
		<>
			{/* Backdrop */}
			<div
				className='fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity'
				onClick={onClose}
			/>

			{/* Drawer */}
			<div className='fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[80vh] overflow-hidden transition-transform transform translate-y-0'>
				<div className='sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between'>
					<h2 className='text-2xl font-glacial text-gray-900'>Available Services</h2>
					<button
						onClick={onClose}
						className='p-2 hover:bg-gray-100 rounded-full transition-colors'
					>
						<FiX className='h-6 w-6 text-gray-600' />
					</button>
				</div>

				<div className='overflow-y-auto max-h-[calc(80vh-80px)] p-6'>
					{availablePrices.length > 0 ? (
						<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
							{availablePrices.map((price) => {
								const icon = serviceIcons.icons[price.product.metadata?.icon];
								const isPremierPackage = price.product.name === 'The Premier Package';
								const isGoldPackage = price.product.name === 'The Gold Package';
								const isPopular = isPremierPackage || isGoldPackage;

								return (
									<div
										key={price.id}
										onClick={() => handleServiceClick(price)}
										className={`relative bg-white rounded-xl shadow-sm border-2 ${
											isPopular
												? 'border-[#CC1DB9]'
												: 'border-gray-200'
										} hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer flex flex-col h-full`}
									>
										{/* Most Popular Badge */}
										{isPopular && (
											<div className='absolute -top-0.5 -right-0.5 bg-[#CC1DB9] text-white px-3 py-1 text-xs font-montserrat-medium uppercase tracking-wide rounded-tr-[0.7rem]'>
												Most Popular
											</div>
										)}

										<div className='p-5'>
											{/* Icon */}
											{icon && (
												<div className={`${isPopular ? 'bg-[#CC1DB9]' : 'bg-[#202020]'} rounded-lg flex items-center justify-center mb-3 shadow-sm w-12 h-12`}>
													<span className='text-xl leading-none flex items-center justify-center'>{icon}</span>
												</div>
											)}

											{/* Service Info */}
											<h3 className='text-xl font-glacial text-gray-900 mb-1.5 leading-tight'>
												{price.product.name}
											</h3>
											{price.nickname && (
												<p className='text-sm font-montserrat-medium text-gray-700 mb-2.5'>
													{price.nickname}
												</p>
											)}

											{/* Price - Moved up */}
											<div className='mb-2.5'>
												<p className='text-3xl font-glacial text-gray-900'>
													${(price.unit_amount / 100).toFixed(2)}
													<span className='text-base font-montserrat-regular text-gray-500'>/month</span>
												</p>
											</div>

											<p className='text-sm font-montserrat-regular text-gray-600 line-clamp-3 leading-relaxed'>
												{price.product.description || 'This is an awesome Jane Rothe Product'}
											</p>
										</div>

										{/* Action Button */}
										<div className='px-5 pb-5 mt-auto'>
											<button
												className={`w-full py-2.5 px-4 rounded-lg font-montserrat-medium text-sm transition-all ${
													isPopular
														? 'bg-[#CC1DB9] text-white hover:bg-pink-600 shadow-md hover:shadow-lg'
														: 'bg-[#202020] text-white hover:bg-gray-800'
												}`}
											>
												Subscribe Now
											</button>
										</div>
									</div>
								);
							})}
						</div>
					) : (
						<div className='text-center py-12'>
							<p className='text-gray-600 font-montserrat-regular'>
								No additional services available at this time.
							</p>
						</div>
					)}
				</div>
			</div>

			{/* Confirmation Dialog */}
			{selectedPrice && (
				<ConfirmationDialog
					open={dialogOpen}
					onClose={handleDialogClose}
					onConfirm={handleSubscribe}
					message={`You want to subscribe to the ${selectedPrice.product.name} service for $${(selectedPrice.unit_amount / 100).toFixed(2)} a month? (Confirm and you will be taken to a payment page)`}
					confirmText="Confirm"
					cancelText="Not now"
				/>
			)}
		</>
	);
};

export default AddServiceDrawer;